import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup, Validators, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NotificationService } from 'src/app/core/services/notification.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { UserService } from 'src/app/core/services/user.services';
import { CashierForm } from 'src/app/core/models/interfaces/forms/cashier.form';
import { TranslateService } from '@ngx-translate/core';
import { CashierModel } from 'src/app/core/models/cashier.model';
import { debounceTime } from 'rxjs/operators';
import { ConfirmarDialogComponent } from 'src/app/shared/components/confirmar-dialog/confirmar-dialog.component';
import { MyCommerceService } from 'src/app/core/services/my-commerce.service';

@Component({
  selector: 'app-create-cashiers-component',
  templateUrl: './create-cashiers.component.html',
  styleUrls: ['./create-cashiers.component.scss']
})
export class CreateCashiersComponent implements OnInit {
  private viewState: ABMState = ABMState.CREATE;
  private readonly CAJAS: string = 'cashiers';
  @ViewChild('branchSelect', { static: true }) branchSelect: MatSelect;

  branchOfficesCollection: SimpleSelect[];
  selectedBranch: SimpleSelect;
  messageValidatorParam;
  form: UntypedFormGroup;
  currentEdition: CashierModel;
  isEdit: boolean = false;
  editado: boolean = false;
  branch: any;
  saving: boolean = false;
  branchId;

  constructor(
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private notificationService: NotificationService,
    private dialog: MatDialog,
    private translateService: TranslateService,
    private userService: UserService,
    private route: ActivatedRoute,
    private myCommerceService: MyCommerceService
  ) {
    this.branchOfficesCollection = new Array<SimpleSelect>();
    this.messageValidatorParam = { campo: this.translate('commons.labels.name') };
    this.form = new UntypedFormGroup({
      name: new UntypedFormControl('', Validators.required),
      paymentOrders: new UntypedFormControl(false, Validators.required),
      branchOffice: new UntypedFormControl(null, Validators.required),
      externalQrId: new UntypedFormControl(null)
    });
  }

  async ngOnInit() {
    // Sucursales
    let officeBranches = await this.userService.getBranchOfficesCollection();
    officeBranches.forEach((ob) => {
      const opt = { value: ob.id, display: ob.name };
      this.branchOfficesCollection.push(opt);
    });

    this.route.queryParams.subscribe(async (val) => {
      if (val.cashier) {
        this.branchId = val.branch;
        this.branch = this.branchOfficesCollection.find((branch) => branch.value == val.branch);
        this.isEdit = true;
        let cashier = await this.myCommerceService.getCashier(val.cashier, val.branch);
        let cashierModel: CashierModel = new CashierModel();

        cashierModel.id = cashier.id;
        cashierModel.name = cashier.nombre;
        cashierModel.externalQrId = cashier.qrExternoId;
        cashierModel.paymentOrders = cashier.trabajaOrdenesPago;

        this.addToForm('name', cashierModel.name);
        this.addToForm('paymentOrders', cashierModel.paymentOrders);
        this.addToForm('externalQrId', cashierModel.externalQrId ? cashierModel.externalQrId : '');
        this.addToForm('branchOffice', {
          value: this.branch.value,
          display: this.branch.display
        });
        this.currentEdition = cashierModel;
        this.viewState = ABMState.EDITION;

        this.form.controls['name'].valueChanges.subscribe(
          (val) => (this.editado = this.validarEdicion(val) ? true : false)
        );
        this.form.controls['paymentOrders'].valueChanges.subscribe(
          (val) => (this.editado = this.validarEdicion(val) ? true : false)
        );
        this.form.controls['branchOffice'].valueChanges.subscribe(
          (val) => (this.editado = this.validarEdicion(val) ? true : false)
        );
        this.form.controls['externalQrId'].valueChanges.subscribe(
          (val) => (this.editado = this.validarEdicion(val) ? true : false)
        );
      }
    });

    this.form
      .get('paymentOrders')
      .valueChanges.pipe(debounceTime(300))
      .subscribe((val) => (!val ? this.form.get('externalQrId').setValue('') : null));
  }

  validarEdicion(val) {
    if (val) {
      return true;
    } else {
      return false;
    }
  }

  compareBranchOffices(selected: SimpleSelect, compare: SimpleSelect): boolean {
    return Number(selected.value) == Number(compare.value);
  }

  addToForm(input: string, value: any): void {
    this.form.get(input).setValue(value);
  }

  getCreateErrorMessage(): string {
    return this.translate('pages.cashiers.messages.create.error');
  }

  getEditErrorMessage(): string {
    return this.translate('pages.cashiers.messages.edit.error');
  }

  getCreateSuccessMessage(): string {
    return this.translate('pages.cashiers.messages.create.success');
  }

  getEditSuccessMessage(): string {
    return this.translate('pages.cashiers.messages.edit.success');
  }

  getBackRoute(): string {
    return '/user/cashiers';
  }

  getConfirmationTitle(): string {
    return this.translate('pages.cashiers.messages.confirm.title');
  }

  getConfirmationText(): string {
    return this.translate('pages.cashiers.messages.confirm.body');
  }

  goBack() {
    this.router.navigate(['/user/my-commerce']);
  }

  isEdition(): boolean {
    return this.viewState == ABMState.EDITION;
  }

  async userConfirmation(): Promise<boolean> {
    return new Promise((resolve) => {
      const dialogConfig = new MatDialogConfig();
      dialogConfig.data = {
        id: 1,
        title: this.getConfirmationTitle(),
        info: this.getConfirmationText()
      };

      const dialogRef = this.dialog.open(ConfirmarDialogComponent, dialogConfig);
      dialogRef.afterClosed().subscribe((response) => {
        resolve(response != '' && response);
      });
    });
  }

  getSuccessMessage(): string {
    if (this.viewState == ABMState.CREATE) {
      return this.getCreateSuccessMessage();
    } else {
      return this.getEditSuccessMessage();
    }
  }

  getErrorMessage(): string {
    if (this.viewState == ABMState.CREATE) {
      return this.getCreateErrorMessage();
    } else {
      return this.getEditErrorMessage();
    }
  }

  async onSubmitEventHandler(form: CashierForm): Promise<void> {
    sessionStorage.removeItem(this.CAJAS);
    try {
      if (this.isEdition()) {
        form.id = this.currentEdition.id;
        const userConfirmation = await this.userConfirmation();
        this.saving = true;
        if (!userConfirmation) {
          this.saving = false;
          return;
        } else {
          this.userService.editPuestoCobro(form, this.branchId).subscribe((data) => {
            if (data) {
              return this.notificationService.showSuccess('Sucursal actualizada correctamente');
            }
          });
          this.saving = false;
          this.goBack();
          return;
        }
      } else {
        this.saving = true;
        let response = await this.userService.saveCashier(form);
        if (response) {
          this.notificationService.showSuccess(this.getSuccessMessage());
          this.goBack();
        } else {
          this.saving = false;
          throw new Error(this.getErrorMessage());
        }
        this.saving = false;
      }
    } catch (error) {
      this.notificationService.showError(error.message);
    }
  }

  translate(key: string): string {
    return this.translateService.instant(key);
  }
}

enum ABMState {
  CREATE = 'C',
  EDITION = 'E'
}

interface SimpleSelect {
  value: number;
  display: string;
}
